"use client";

import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "../lib/utils";

/**
 * Clamp a caller-supplied percentage into the [0, 100] range the indicator's
 * `translateX` maths assumes.
 *
 * An out-of-range value used to reach the template literal below and produce a
 * malformed declaration (`translateX(--33.33%)` for 133.33), which the browser
 * either drops on mount — leaving the bar full — or, on an update, silently
 * ignores, leaving the bar stuck at whatever it rendered first.
 *
 * `null`/`undefined` stay indeterminate. A non-finite number (`NaN`,
 * `±Infinity`) is *not* a meaningful position, so it is treated as
 * indeterminate too rather than being coerced to 0.
 */
function clampProgressValue(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

/**
 * Determinate horizontal bar for a known 0–100 value. For an unknown wait use `Spinner` or `Skeleton` (see the Loading Indicators taxonomy); for step position, count steps in text.
 */
function Progress({
  className,
  value,
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root>) {
  const clampedValue = clampProgressValue(value);

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(
        "bg-primary/20 relative h-2 w-full overflow-hidden rounded-full",
        className,
      )}
      value={clampedValue}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className="bg-primary h-full w-full flex-1 transition-all"
        style={{ transform: `translateX(-${100 - (clampedValue ?? 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  );
}

export { Progress };
